import Button from '@/components/Button';
import Modal from '@/components/Modal';
import Row from '@/components/Row';
import { ChainId, NETWORK_CONFIG } from '@/constant';
import { getServerApi } from '@/gql';
import user from '@/stores/user';
import { Checkbox, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    max-width: 480px;
`;

const EntityInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 16px;
`;

const EntityImage = styled.img`
    width: 80px;
    height: 80px;
    border-radius: 12px;
    object-fit: cover;
`;

const EntityName = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #000;
    text-align: center;
`;

const DatAddress = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #e9ecef;
    border-radius: 8px;
    font-size: 14px;
    color: #6c757d;
`;

const DatAddressValue = styled.span`
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;
`;

const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Label = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: #000;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const CheckboxWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    color: rgba(143, 143, 143, 1);

    .MuiCheckbox-root {
        padding: 0 !important;

        svg {
            width: 18px;
            height: 18px;
        }

        &.Mui-checked {
            color: rgba(0, 186, 16, 1);
        }
    }
`;

const StyledTextField = styled(TextField)`
    .MuiOutlinedInput-root {
        border-radius: 12px;
        background: #fff;

        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: #007bff;
        }

        .MuiOutlinedInput-notchedOutline {
            border-color: #e9ecef;
        }
    }

    .MuiInputBase-input {
        padding: 16px;
        font-size: 14px;
    }
`;

const ErrorText = styled.div`
    font-size: 12px;
    color: #dc3545;
    margin-top: 4px;
`;

interface RenameDatProps {
    isOpen: boolean;
    onClose: () => void;
    name: string;
    datAddress: string;
    onConfirmSuccess: () => void;
    uuid: string;
    avatar: string;
    tokenId: number;
}

interface FormData {
    newName: string;
    yourName: string;
}

export default function RenameDat({
    isOpen,
    onClose,
    name,
    datAddress,
    onConfirmSuccess,
    uuid,
    avatar,
    tokenId,
}: RenameDatProps) {
    const [keepOldName, setKeepOldName] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormData>({
        defaultValues: {
            newName: '',
            yourName: '',
        },
    });

    const newName = watch('newName');

    useEffect(() => {
        if (isOpen) {
            setValue('newName', '');
            setValue('yourName', '');
            setKeepOldName(false);
            setSubmitError(null);
        }
    }, [isOpen, name, setValue]);

    const handleKeepOldNameChange = (checked: boolean) => {
        setKeepOldName(checked);
        if (checked) {
            setValue('newName', name);
        }
    };

    // Monitor newName changes and uncheck "Keep old name" if user modifies the input
    useEffect(() => {
        if (keepOldName && newName !== name && newName !== '') {
            setKeepOldName(false);
        }
    }, [newName, name, keepOldName]);

    const onSubmit = async (data: FormData) => {
        setSubmitError(null);
        try {
            const api = getServerApi();
            const res = await api.setNames({
                uuid: uuid,
                name: data.newName,
                masterName: data.yourName,
            });
            if (res && res.setNames) {
                onConfirmSuccess();
                onClose();
            } else {
                setSubmitError('Rename failed. Please try again.');
            }
        } catch (e: any) {
            const message =
                e?.response?.errors?.[0]?.message ||
                e?.message ||
                'Unknown error';
            setSubmitError(message);
        }
    };

    const formatDatAddress = (address: string) => {
        if (address.length <= 12) return address;
        return `${address.slice(0, 6)}...${address.slice(-6)}`;
    };

    const onClickDat = () => {
        const explorer =
            NETWORK_CONFIG[user.chainId || ChainId.testnet].explore;
        window.open(`${explorer}address/${datAddress}`, '_blank');
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Rename Lazbubu`}>
            <ModalContent>
                <EntityInfo>
                    <EntityImage
                        src={avatar}
                        alt={name}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                        }}
                    />
                    <EntityName>{name}</EntityName>
                    <DatAddress onClick={onClickDat}>
                        <span>Token ID:</span>
                        <DatAddressValue>{tokenId}</DatAddressValue>
                    </DatAddress>
                </EntityInfo>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormSection>
                        <InputWrapper>
                            <Label>
                                Give a new name{' '}
                                <CheckboxWrapper>
                                    <Checkbox
                                        checked={keepOldName}
                                        onChange={(e) =>
                                            handleKeepOldNameChange(
                                                e.target.checked,
                                            )
                                        }
                                        color="primary"
                                    />
                                    Keep old name
                                </CheckboxWrapper>
                            </Label>
                            <Controller
                                name="newName"
                                control={control}
                                rules={{
                                    required: 'New name is required',
                                    minLength: {
                                        value: 2,
                                        message:
                                            'Name must be at least 2 characters',
                                    },
                                    maxLength: {
                                        value: 50,
                                        message:
                                            'Name must be less than 50 characters',
                                    },
                                }}
                                render={({ field }) => (
                                    <StyledTextField
                                        {...field}
                                        placeholder="Enter new name"
                                        variant="outlined"
                                        fullWidth
                                        error={!!errors.newName}
                                        helperText={errors.newName?.message}
                                    />
                                )}
                            />
                        </InputWrapper>

                        <InputWrapper>
                            <Label>Tell Lazbubu your name</Label>
                            <Controller
                                name="yourName"
                                control={control}
                                rules={{
                                    required: 'Your name is required',
                                    minLength: {
                                        value: 2,
                                        message:
                                            'Name must be at least 2 characters',
                                    },
                                    maxLength: {
                                        value: 50,
                                        message:
                                            'Name must be less than 50 characters',
                                    },
                                }}
                                render={({ field }) => (
                                    <StyledTextField
                                        {...field}
                                        placeholder="Enter your name"
                                        variant="outlined"
                                        fullWidth
                                        error={!!errors.yourName}
                                        helperText={errors.yourName?.message}
                                    />
                                )}
                            />
                        </InputWrapper>
                    </FormSection>

                    {submitError && (
                        <ErrorText role="alert" style={{ marginTop: '8px' }}>
                            {submitError}
                        </ErrorText>
                    )}
                    <Row justify="center" style={{ marginTop: '24px' }}>
                        <Button
                            type="primary"
                            size="normal"
                            onClick={handleSubmit(onSubmit)}
                            style={{ width: '100%' }}
                        >
                            Done
                        </Button>
                    </Row>
                </form>
            </ModalContent>
        </Modal>
    );
}
